import React from 'react';
import { Seo } from '../components/Seo';
import { ButtonLink, Eyebrow, Heading, NextPage, Prose, Section, TextLink } from '../components/ui';
import { images, site } from '../data/site';
const anchors = [{
  id: 'portemonnees',
  label: 'Portemonnees'
}, {
  id: 'pasjeshouders',
  label: 'Pasjeshouders'
}, {
  id: 'horeca',
  label: 'Horeca lederwaren'
}, {
  id: 'riemen',
  label: 'Leren riemen'
}, {
  id: 'sleuteletuis',
  label: 'Sleuteletuis'
}, {
  id: 'accessoires',
  label: 'Leren accessoires'
}];
function Block({
  id,
  eyebrow,
  title,
  children
}: {
  id: string;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="scroll-mt-24 border-t border-cocoa/15 pt-10" data-id="element-132" data-mp="28goig6to369z">
      <Eyebrow data-id="element-133" data-mp="23mu13a1jb3e2">{eyebrow}</Eyebrow>
      <Heading className="max-w-3xl" data-id="element-134" data-mp="1fyarmr6r0kke">{title}</Heading>
      <div className="mt-5 grid gap-8 lg:grid-cols-2 lg:gap-14" data-id="element-135" data-mp="1fwzpevsybkve">{children}</div>
    </section>;
}
function Voor({
  items
}: {
  items: string[];
}) {
  return <div className="rounded-2xl bg-sand/60 p-6" data-id="element-136" data-mp="ia2tnzz49z19">
      <h3 className="font-display text-lg font-semibold text-ink" data-id="element-137" data-mp="i9axamxp3x9x">Geschikt voor</h3>
      <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-cocoa/85" data-id="element-138" data-mp="m9lbunzjzw0e">
        {items.map(i => <li key={i} className="flex gap-3" data-id="element-139" data-mp="r2mth3r5a9no">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-pine" aria-hidden="true" data-id="element-140" data-mp="w1haq19grq5o" />
            <span data-id="element-141" data-mp="3vgkzbnhnnvea">{i}</span>
          </li>)}
      </ul>
    </div>;
}
export function Collectie() {
  return <>
      <Seo title="Collectie LeonDesign | Leren portemonnees, pasjeshouders & horeca lederwaren" description="De collectie van LeonDesign per categorie: leren portemonnees, pasjeshouders, horeca portemonnees, riemen, sleuteletuis, heuptasjes, paspoorthoesjes en pennenzakken." data-id="element-142" data-mp="3dbsbl5vud90" />

      <Section className="pt-12 sm:pt-16" data-id="element-143" data-mp="1jpqrgortlnpy">
        <div className="max-w-3xl" data-id="element-144" data-mp="1jswfe0opqjk3">
          <Eyebrow data-id="element-145" data-mp="1ootwwabppvyg">Collectie & toepassingen</Eyebrow>
          <h1 className="font-display text-4xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-5xl" data-id="element-146" data-mp="1orptnvbtqelb">
            Welk leren artikel past bij welk gebruik?
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cocoa/90" data-id="element-147" data-mp="u24v1bb2j1rs">
            De collectie van LeonDesign is opgebouwd rond dagelijks gebruik. Hieronder staat per
            categorie wat het artikel doet, waar het voor gemaakt is en wie er doorgaans baat bij
            heeft. Wilt u eerst weten wie er achter de webshop zit? Dat leest u op de pagina{' '}
            <TextLink to="/over-ons" data-id="element-148" data-mp="bcoifpm3ziv0">over LeonDesign</TextLink>.
          </p>
        </div>

        <nav aria-label="Snel naar een categorie" className="mt-8" data-id="element-149" data-mp="q1b9gol3lz22">
          <ul className="flex flex-wrap gap-2" data-id="element-150" data-mp="q2mdmpigh8tp">
            {anchors.map(a => <li key={a.id} data-id="element-151" data-mp="l7zx67xioq2r">
                <a href={`#${a.id}`} className="inline-block rounded-full border border-cocoa/20 px-4 py-2 text-[15px] font-medium text-cocoa transition-colors duration-150 ease-out hover:border-pine hover:text-pine" data-id="element-152" data-mp="3tpmp0fdep9y5">
                  {a.label}
                </a>
              </li>)}
          </ul>
        </nav>

        <figure className="mt-10 overflow-hidden rounded-3xl border border-cocoa/10 bg-paper" data-id="element-153" data-mp="291aifi3ivrbr">
          <img src={images.cover} alt="Leren portemonnees, pasjeshouders en paspoorthoesjes uit de collectie van LeonDesign" className="w-full object-cover" width={1600} height={600} data-id="element-154" data-mp="1u8qfahgdhjub" />
        </figure>
      </Section>

      <Section className="pt-16 sm:pt-20" data-id="element-155" data-mp="2rw8zqpjecae5">
        <div className="space-y-16" data-id="element-156" data-mp="2dz3n26p6gd9x">
          <Block id="portemonnees" eyebrow="Portemonnees" title="Leren portemonnees voor dagelijks gebruik" data-id="element-157" data-mp="2dy0tts4dtyqu">
            <Prose data-id="element-158" data-mp="2hycg19ce874f">
              <p data-id="element-159" data-mp="2i20acapnwxk7">
                De portemonnee is het hart van de collectie. Er zijn compacte modellen voor wie
                alleen pasjes en wat briefgeld meeneemt, klassieke overslagmodellen met een ruim
                muntvak, en grotere dames- en herenportemonnees met plek voor bonnetjes,
                klantenkaarten en biljetten.
              </p>
              <p data-id="element-160" data-mp="1hzu1hjn6yhaj">
                Het verschil zit vooral in de indeling. Een portemonnee met te veel vakken raakt
                gevuld met spullen die u nooit gebruikt en wordt dik; een te krappe portemonnee
                dwingt u tot uitpuilen. Tel daarom vooraf hoeveel pasjes u dagelijks nodig heeft en
                of u contant geld bij u draagt.
              </p>
              <p data-id="element-161" data-mp="20rmqz431xkdi">
                Leer heeft daarbij een praktisch voordeel: het rekt licht mee met de inhoud en
                behoudt zijn vorm. Na een paar maanden voelt een leren portemonnee daardoor
                persoonlijker dan op de dag van aanschaf.
              </p>
            </Prose>
            <Voor items={['Wie een dagelijkse portemonnee zoekt die jaren meegaat in plaats van seizoenen', 'Mensen die zowel pasjes als contant geld bij zich dragen', 'Cadeauzoekers die iets willen geven dat blijvend gebruikt wordt']} data-id="element-162" data-mp="kepn0fgdynd1" />
          </Block>

          <Block id="pasjeshouders" eyebrow="Pasjeshouders" title="Pasjeshouders van leer voor wie licht wil reizen" data-id="element-163" data-mp="2vyx47sxskbbn">
            <Prose data-id="element-164" data-mp="35qt9bnyap1pd">
              <p data-id="element-165" data-mp="35r2xr73oz0sy">
                Wie vrijwel alles pint, heeft geen volledige portemonnee meer nodig. Een pasjeshouder
                van leer houdt bank-, ID- en klantenkaarten bij elkaar en verdwijnt makkelijk in een
                binnenzak of jaszak. Sommige modellen hebben een klein muntvak of een ruimte voor
                enkele opgevouwen biljetten.
              </p>
              <p data-id="element-166" data-mp="3opf98zi2smrp">
                Let bij de keuze op de manier waarop u de pasjes eruit haalt. Uitschuifmodellen
                werken snel, gestapelde vakken bieden meer overzicht. Beide werken prima, zolang het
                leer soepel genoeg is om de pasjes niet klem te zetten.
              </p>
            </Prose>
            <Voor items={['Forensen en zakelijke reizigers met weinig contant geld', 'Wie een tweede, compacte houder wil naast de gewone portemonnee', 'Gebruikers die de pasjes gescheiden willen houden van munten en bonnen']} data-id="element-167" data-mp="2xls6v5xzjtdk" />
          </Block>

          <Block id="horeca" eyebrow="Horeca lederwaren" title="Horeca portemonnees, holsters en werkleer voor de professional" data-id="element-168" data-mp="31mu5plnzq3f0">
            <div className="space-y-6" data-id="element-169" data-mp="3bljqbm79u1yy">
              <figure className="overflow-hidden rounded-2xl border border-cocoa/12" data-id="element-170" data-mp="3bkial4fb8raw">
                <img src={images.horecaBar} alt="Leren horeca portemonnees en holsters van LeonDesign op een bar met telefoon en bonnenblok" className="w-full object-cover" loading="lazy" data-id="element-171" data-mp="3pfu4xapk0fra" />
              </figure>
              <Prose data-id="element-172" data-mp="3uaq1mo5qjraq">
                <p data-id="element-173" data-mp="hthvj3tkkhxv">
                  Een horeca portemonnee wordt tijdens één dienst tientallen keren geopend. De
                  indeling is daarom anders dan bij een particuliere portemonnee: brede muntvakken
                  die met één hand te bedienen zijn, een ruime bonnenhouder en een aparte plek voor
                  briefgeld. Een holster of heupetui houdt telefoon, pen en bonnenblok binnen
                  handbereik zonder dat u het schort hoeft te doorzoeken.
                </p>
                <p data-id="element-174" data-mp="3t74o0s9f3cp">
                  Omdat het materiaal dagelijks tegen randen, tafels en dienbladen aankomt, telt
                  slijtvastheid zwaarder dan uiterlijk. Leer heeft daarbij het voordeel dat
                  gebruikssporen er niet slordig uitzien, maar juist bij het materiaal horen.
                </p>
              </Prose>
            </div>
            <div className="space-y-6" data-id="element-175" data-mp="3ca26re18gjpq">
              <figure className="overflow-hidden rounded-2xl border border-cocoa/12" data-id="element-176" data-mp="3gam4ojtmrrcx">
                <img src={images.horecaTerras} alt="Bediening met een leren heupholster van LeonDesign op een terras" className="w-full object-cover" loading="lazy" data-id="element-177" data-mp="kz1dnp9jj12" />
              </figure>
              <Voor items={['Bedienend personeel in restaurants, cafés en op terrassen', 'Taxichauffeurs die contant geld en bonnen gescheiden willen houden', 'Kappers en andere vakmensen die klein gereedschap en betaalmiddelen bij zich dragen', 'Horecazaken die hun team in één keer van hetzelfde materiaal willen voorzien']} data-id="element-178" data-mp="3mua45v3o0lbu" />
            </div>
          </Block>

          <Block id="riemen" eyebrow="Riemen" title="Leren riemen: dagelijks dragen en zakelijk gebruik" data-id="element-179" data-mp="2z45xtfz3e7h5">
            <Prose data-id="element-180" data-mp="2u2qcrnwx00yr">
              <p data-id="element-181" data-mp="2u5cp00ur34nb">
                Een leren riem is een van de weinige kledingstukken waarbij kwaliteit meteen voelbaar
                is. Stug, dik leer houdt zijn vorm en rekt nauwelijks uit; een goedkope riem gaat na
                een halfjaar bij de gaatjes scheuren. De collectie bevat klassieke breedtes en
                kleuren die bij zowel een nette broek als een spijkerbroek te dragen zijn.
              </p>
              <p data-id="element-182" data-mp="1jk6q22jjizzs">
                Voor professioneel gebruik telt nog iets extra’s: als er een holster of heuptasje aan
                de riem hangt, moet de riem dat gewicht dragen zonder door te zakken. Kies in dat
                geval voor een steviger uitvoering en een gesp die niet losschiet.
              </p>
            </Prose>
            <Voor items={['Dagelijkse dragers die een riem jaren willen gebruiken', 'Horeca- en taxipersoneel dat een etui of holster aan de riem draagt', 'Wie een neutrale riem zoekt die bij meerdere gelegenheden past']} data-id="element-183" data-mp="vshvefj8xzfo" />
          </Block>

          <Block id="sleuteletuis" eyebrow="Sleuteletuis" title="Sleuteletuis die krassen en rinkelen voorkomen" data-id="element-184" data-mp="825g2jlo90f2">
            <Prose data-id="element-185" data-mp="ifp6prt6uzs2">
              <p data-id="element-186" data-mp="if5xr0linlq6">
                Losse sleutels in een tas of broekzak zijn de meest voorkomende oorzaak van krassen op
                een telefoonscherm of op het leer van een portemonnee. Een leren sleuteletui vangt dat
                op: de sleutels zitten binnenin aan ringen of haken, de buitenkant is zacht.
              </p>
              <p data-id="element-187" data-mp="3qxlmzieh5ir1">
                Voor wie veel sleutels bij zich draagt — bijvoorbeeld beheerders, chauffeurs of
                horecapersoneel met toegang tot meerdere ruimtes — is een etui ook overzichtelijker
                dan een bos aan één ring.
              </p>
            </Prose>
            <Voor items={['Gebruikers met meerdere sleutels die deze gescheiden willen houden', 'Wie krassen op telefoon, bril of portemonnee wil voorkomen', 'Zakelijke gebruikers met sleutels van meerdere locaties']} data-id="element-188" data-mp="9gty692o9dll" />
          </Block>

          <Block id="accessoires" eyebrow="Leren accessoires" title="Heuptasjes, paspoorthoesjes en pennenzakken" data-id="element-189" data-mp="1u6wvpjpvkf4e">
            <Prose data-id="element-190" data-mp="1l701z3omoq2h">
              <p data-id="element-191" data-mp="1lanxu9hjpokd">
                <strong className="font-semibold text-ink" data-id="element-192" data-mp="1g9gpo9bhqnd6">Heuptasjes</strong> houden uw handen vrij
                en uw spullen dichtbij. Handig op een markt, festival of tijdens een lange werkdag,
                maar door de rustige vormgeving ook prima als dagelijkse tas voor telefoon, sleutels
                en portemonnee.
              </p>
              <p data-id="element-193" data-mp="12iv3aamtvvaj">
                <strong className="font-semibold text-ink" data-id="element-194" data-mp="12iblqv13b8x1">Paspoorthoesjes</strong> beschermen de kaft
                van een paspoort tegen ezelsoren en vocht in een tas. Veel modellen bieden daarnaast
                plek voor instapkaarten, een pasje of een vaccinatieboekje, zodat reisdocumenten bij
                elkaar blijven.
              </p>
              <p data-id="element-195" data-mp="dwnnq3fseghd">
                <strong className="font-semibold text-ink" data-id="element-196" data-mp="dw5gycvi0jv7">Pennenzakken en etuis</strong> van leer zijn
                bedoeld voor wie pennen, oplaadkabels of klein gereedschap bij elkaar wil houden. In
                een tas of bureaulade is dat een klein verschil dat dagelijks merkbaar is.
              </p>
            </Prose>
            <Voor items={['Reizigers die documenten en pasjes bij elkaar willen houden', 'Wie tijdens werk of evenementen zijn handen vrij wil hebben', 'Studenten, professionals en vakmensen met klein materiaal in de tas']} data-id="element-197" data-mp="8ydpfjyrmzrj" />
          </Block>
        </div>
      </Section>

      <Section className="pt-20 sm:pt-24" data-id="element-198" data-mp="rvws85umrm5i">
        <div className="rounded-3xl bg-ink px-6 py-12 text-cream sm:px-12" data-id="element-199" data-mp="ruvj46x6d9qf">
          <div className="max-w-2xl" data-id="element-200" data-mp="1fkzlkaymn3fu">
            <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl" data-id="element-201" data-mp="1kd95nad299a7">
              Niet zeker welk model past bij uw gebruik?
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-cream/85" data-id="element-202" data-mp="351v3rznn174e">
              Beschrijf kort hoe u het artikel gaat gebruiken — dagelijks in de broekzak, achter de
              bar, in de taxi of in de salon. Op basis daarvan is de keuze meestal snel te maken.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-203" data-mp="39uwm6hn1zosq">
              <ButtonLink href={site.phoneHref} className="!bg-sage !text-ink hover:!bg-cream" data-id="element-204" data-mp="2m4acyn1bbyh5">
                Bel {site.phone}
              </ButtonLink>
              <ButtonLink href={`mailto:${site.email}`} className="!border !border-cream/30 !bg-transparent !text-cream hover:!bg-cream/10" data-id="element-205" data-mp="2h7jwvzpid2vq">
                Mail uw vraag
              </ButtonLink>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3" data-id="element-206" data-mp="bb7lp6kblhu6">
          <NextPage to="/" label="Homepage" description="Introductie op LeonDesign en de belangrijkste productgroepen in het kort." data-id="element-207" data-mp="6e7g0wrduh3l" />
          <NextPage to="/bronnen" label="Bronnen & vermeldingen" description="Bedrijfsprofielen, lokale citaties en kaartdiensten, geordend per categorie." data-id="element-208" data-mp="2h0rok4rg63xu" />
          <NextPage to="/contact" label="Contact opnemen" description="Telefoon, e-mail, adres in Geleen en de bereikbaarheid van de webshop." data-id="element-209" data-mp="2q1hj754z1o9z" />
        </div>
      </Section>
    </>;
}